import React, { createContext, PropsWithChildren } from 'react';
import { SnackbarKey, SnackbarProvider } from 'notistack';
import { DeepPartial, RsiProps } from '../types';
import { BpDesignThemeProvider, Button, createBpDesignTheme, THEME_ID } from '@beeline.com/bp-design-system';
import { BpIntlProvider } from '@beeline.com/bp-intl';
import messages from '../__generated__/lang/en.public.intl.output.json';
import { useRsiTranslations } from '../useRsiTranslations';
import { RsiTranslations } from '../translations/RsiTranslations.types';

const bpDesignTheme = createBpDesignTheme();
export const RsiContext = createContext({} as any);

type ProvidersProps<T extends string> = {
  children: React.ReactNode;
  rsiValues: RsiProps<T>;
  translations: DeepPartial<RsiTranslations>;
};

export const Providers = <T extends string>({ children, rsiValues, translations = {} }: ProvidersProps<T>) => {
  const notistackRef = React.useRef<SnackbarProvider | null>(null);
  const onClickDismiss = (snackbarId: SnackbarKey | undefined) => () => {
    notistackRef?.current?.closeSnackbar(snackbarId);
  };

  if (!rsiValues.fields) {
    throw new Error('Fields must be provided to react-spreadsheet-import');
  }

  return (
    <BpIntlProvider messages={messages} supportedLanguages={['en']}>
      <RsiContextProvider value={rsiValues} translations={translations}>
        <BpDesignThemeProvider theme={{ [THEME_ID]: bpDesignTheme }}>
          <SnackbarProvider
            // After migration to MUI v5 this line can be removed. It is required for MUI v4
            // https://github.com/iamhosseindhv/notistack/issues/394#issuecomment-877688487
            style={{ pointerEvents: 'all', margin: '0.5rem' }}
            maxSnack={3}
            data-test="notistack-snackbar"
            ref={notistackRef}
            action={(key) => (
              <Button
                style={{ color: 'white' }}
                variant="text"
                onClick={onClickDismiss(key)}
                dataTest="snackbar__notification__button"
              >
                Dismiss
              </Button>
            )}
          >
            {children}
          </SnackbarProvider>
        </BpDesignThemeProvider>
      </RsiContextProvider>
    </BpIntlProvider>
  );
};

function RsiContextProvider<T extends string>({
  children,
  translations,
  value
}: PropsWithChildren<{ value: RsiProps<T>; translations: DeepPartial<RsiTranslations> }>) {
  const mergedTranslations = useRsiTranslations(translations ?? {});
  return <RsiContext.Provider value={{ ...value, translations: mergedTranslations }}>{children}</RsiContext.Provider>;
}
